/**
 * ProtectedRoute — renders the nested routes only for a signed-in user.
 *
 * While `/api/auth/me` is still in flight it shows a neutral loading state
 * rather than bouncing to /login, so a slow first paint does not log the
 * operator out of a page they are already authorised for.
 */
import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isEmbedded } from "../lib/embedBridge";

export default function ProtectedRoute({ requiredRole = null }) {
  const { user, loading, isRole } = useAuth();
  const location = useLocation();

  // Inside the dashboard's CRM frame there is no login screen: EmbedBridge asks
  // the dashboard to sign in again and this neutral state shows meanwhile.
  if (loading || (!user && isEmbedded())) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground" data-testid="auth-loading">
        <div className="text-sm">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requiredRole && !isRole(requiredRole)) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground" data-testid="access-denied">
        <div className="text-sm">Access denied — requires {requiredRole} role.</div>
      </div>
    );
  }

  return <Outlet />;
}
